import { types, flow, applySnapshot, getSnapshot } from "mobx-state-tree";
import { PUBLISH_STATUSES } from "../constants/models";
import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";
import { MSURL } from "utils/ms";
import { isEmptyObject } from "../utils";
import { FILTER_COLUMNS } from "../constants/filterConstants";
import { getNextPublishStatusWithoutScheduled } from "../utils/publish";

const workoutsPath = "/workouts/api/v1";
export const TrainerModel = types
  .model({
    id: types.number,
    title: types.string,
    description: types.maybeNull(types.string),
    photo: types.maybeNull(types.string),
    trainingCategories: types.maybeNull(types.string),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
        file: self.photo,
      };
    },
  }));

const TrainerStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${workoutsPath}/trainer`, params, undefined, MSURL);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: TrainerModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(TrainerModel),
    isMoreLoading: types.optional(types.boolean, false),
    isTrainerLoading: types.optional(types.boolean, false),
    isTrainerManaging: types.optional(types.boolean, false),
    isTrainerPublishing: types.optional(types.boolean, false),
    isTrainerCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    updateTrainer(id: number, data: any) {
      const { items } = getSnapshot(self);

      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isTrainerLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);
          const { items } = yield api.get(
            `${workoutsPath}/trainer`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTrainerLoading = false;
      }
    }),

    fetchDetails: flow(function*(id?: number) {
      self.isTrainerLoading = true;

      try {
        const details = yield api.get(
          `${workoutsPath}/trainer/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTrainerLoading = false;
      }
    }),

    createTrainer: flow(function*(payload: any) {
      self.isTrainerManaging = true;

      try {
        const { id } = yield api.post(
          `${workoutsPath}/trainer`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTrainerManaging = false;
      }
    }),

    editTrainerInfo: flow(function*(payload: any) {
      self.isTrainerManaging = true;

      try {
        return yield api.put(
          `${workoutsPath}/trainer/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTrainerManaging = false;
      }
    }),

    editTrainerPhoto: flow(function*(payload: any) {
      self.isTrainerManaging = true;

      try {
        return yield api.postFormData(
          `${workoutsPath}/trainer/${payload.id}/upload-image`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTrainerManaging = false;
      }
    }),

    changeStatus: flow(function*(id: number, status: string) {
      self.isTrainerPublishing = true;

      try {
        const newStatus = getNextPublishStatusWithoutScheduled(status);
        if (newStatus) {
          const { success } = yield api.post(
            `${workoutsPath}/trainer/${id}/status`,
            {
              status: newStatus,
            },
            undefined,
            MSURL,
          );
          if (success) {
            self.updateTrainer(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTrainerPublishing = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export default TrainerStore.create();
